import React, { useState, useEffect, useCallback, useRef, useContext } from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { DataGrid, GridToolbar, GridFooterContainer, GridPagination } from "@mui/x-data-grid";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent as MuiCardContent,
  Divider,
  Button,
  IconButton,
  CircularProgress,
  Paper,
  Tabs,
  Tab,
  Tooltip,
  FormControl,
  // InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { spacing } from "@mui/system";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import ViewCustomerInvoiceUploadDialog from "../../components/ViewCustomerInvoiceUploadDialog";
import { getThemeTextColor } from "../../utils/themeUtils";
import { useTheme } from "@mui/material/styles";
import apiUtils from "../../utils/apiUtils";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { LinearProgress, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
// import DeleteIcon from "@mui/icons-material/Delete";
import moment from "moment";
import { GlobalContext } from "../../contexts/GlobalContext";

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Card = styled(MuiCard)(spacing);
const CardContent = styled(MuiCardContent)(spacing);

const customerFileActionLabels = {
  0: "Uploaded to OCR System",
  1: "Processed By DWS",
  2: "Moved to Manual Invoices",
  3: "Deleted from Manual Invoices",
};

const CustomerDataGrid = () => {
  const theme = useTheme();
  const { globalData, fetchUserData } = useContext(GlobalContext);
  const [tableData, setTableData] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [viewCustomerInvoiceDialog, setViewCustomerInvoiceDialog] = useState({
    isOpen: false,
    downloadFile: false,
    params: {},
  });
  const [rightPaneSize, setRightPaneSize] = useState(0); // Initial size is 0 (hidden)
  const [lastRightPaneSize, setLastRightPaneSize] = useState(0.5); // Default to 50%
  const [highlightedRowId, setHighlightedRowId] = useState(null);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);

  const containerRef = useRef(null); // Reference to the container div

  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState([]);
  const [isMoveToManualDialogOpen, setIsMoveToManualDialogOpen] = useState(false);
  const [movingToManualInvoices, setMovingToManualInvoices] = useState(false);
  const [moveToManualResults, setMoveToManualResults] = useState([]);
  const [downloadInProgress, setDownloadInProgress] = useState(false);
  // const [fileProgress, setFileProgress] = useState({});
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  const supplierData = apiUtils.useApiData("/api/v1/suppliers");
  const currencyData = apiUtils.useApiData("/api/v1/currencies");
  const productData = apiUtils.useApiData("/api/v1/productTypes");
  const invoiceStateData = apiUtils.useApiData("/api/v1/invoiceStates");
  const countryData = apiUtils.useApiData("/api/v1/countries");
  const customerData = apiUtils.useApiData("/api/v1/entities");

  const fetchCustomerFiles = useCallback(async () => {
    try {
      const response = await axios.get("/api/v1/CustomerFile");
      setTableData(response.data);
    } catch (error) {
      console.error("Error fetching customer files:", error);
    }
  }, []);

  useEffect(() => {
    fetchCustomerFiles();
  }, [fetchCustomerFiles]);

  const columns = [
    { field: "fileId", headerName: "File ID", flex: 0.5, sortable: true, hide: true },
    {
      field: "entityId",
      headerName: "Customer Number",
      flex: 1.5,
      sortable: true,
      valueGetter: (params) => {
        const entity = customerData.find((item) => item.entityId === params.row.entityId);
        return entity ? entity.entityNumber : "Unknown";
      },
    },
    { field: "fileName", headerName: "File Name", flex: 1.5, sortable: true },
    { field: "mimeType", headerName: "Mime Type", flex: 0.5, sortable: true },
    {
      field: "creationDate",
      headerName: "Upload Date",
      flex: 1,
      sortable: true,
      valueGetter: (params) => new Date(params.value),
      valueFormatter: (params) => {
        const date = new Date(params.value);
        return new Intl.DateTimeFormat("nl-NL", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        }).format(date);
      },
    },
    ...(tabValue === 1
      ? [
          {
            field: "processDate",
            headerName: "Process Date",
            flex: 1,
            sortable: true,
            valueGetter: (params) => new Date(params.value),
            valueFormatter: (params) => {
              const date = new Date(params.value);
              return new Intl.DateTimeFormat("nl-NL", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
              }).format(date);
            },
          },
          {
            field: "processedAction",
            headerName: "Action",
            flex: 1,
            sortable: true,
            valueGetter: (params) => customerFileActionLabels[params.value] || "",
          },
          {
            field: "processedByDWSByUserName",
            headerName: "Processed By",
            flex: 1,
            sortable: true,
            valueGetter: (params) => params.row.processedByDWSByUserName || params.row.processedByDWSBy || "",
          },
        ]
      : []),
    { field: "fileSize", headerName: "File Size", flex: 1, sortable: true },
  ];

  // Shared function to open row details
  const openRowDetails = (params) => {
    setHighlightedRowId(params.id);
    setViewCustomerInvoiceDialog({
      isOpen: true,
      downloadFile: true,
      params: params.row,
    });
    setRightPaneSize(lastRightPaneSize); // Open the right pane with the last size
  };

  const handleRowClick = (params, event) => {
    event.defaultMuiPrevented = true; // Prevent default selection behavior
    openRowDetails(params);
  };

  const handleUploadClick = () => {
    const selectedUploadFiles = selectedFiles
      .map((fileId) => {
        const fileData = tableData.find((file) => file.fileId === fileId);
        if (!fileData) return null;

        const entity = customerData.find((item) => item.entityId === fileData.entityId);
        const customerNumber = entity?.entityNumber || fileData.entityId;
        const customerName = String(entity?.name || "").trim();

        return {
          id: fileData.fileId,
          customerLabel: customerName ? `${customerNumber} - ${customerName}` : customerNumber,
          fileName: fileData.fileName,
          fileSize: fileData.fileSize,
          mimeType: fileData.mimeType,
          progress: 0,
          uploading: false,
          uploaded: false,
          error: null,
        };
      })
      .filter(Boolean);

    setUploadResults(selectedUploadFiles);
    setIsUploadDialogOpen(true);
  };

  const [setProcessedByDwsInProgress, setSetProcessedByDwsInProgress] = useState(false);

  const handleProcessedByDWSClick = async () => {
    setSetProcessedByDwsInProgress(true);
    // const processedByDWS = {
    //   processedByDWS: 1,
    //   processDate: moment().format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z",
    // };

    const processedFiles = selectedFiles.map(async (fileId) => {
      const fileData = tableData.find((file) => file.fileId === fileId);
      if (!fileData) return;

      try {
        const updateCustomerFile = await axios.put(`/api/v1/CustomerFileSetProcessedByDws/${fileId}`);
        console.log("Updated Customer File:", updateCustomerFile.data);

        // Update the tableData to remove the uploaded file
        setTableData((prevData) =>
          prevData.map((file) =>
            file.fileId === fileId
              ? { ...file, processedByDWS: 1, processDate: moment().format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z" }
              : file
          )
        );
      } catch (error) {
        console.error(`Error updating file with ID ${fileId}:`, error);
      }
    });

    await Promise.all(processedFiles);
    await fetchCustomerFiles();
    setSelectedFiles([]);
    setSetProcessedByDwsInProgress(false);
  };

  const handleOpenMoveToManualDialog = () => {
    const selectedMoveFiles = selectedFiles
      .map((fileId) => {
        const fileData = tableData.find((file) => file.fileId === fileId);
        if (!fileData) return null;

        const entity = customerData.find((item) => item.entityId === fileData.entityId);
        const customerNumber = entity?.entityNumber || fileData.entityId;
        const customerName = String(entity?.name || "").trim();

        return {
          id: fileData.fileId,
          customerLabel: customerName ? `${customerNumber} - ${customerName}` : customerNumber,
          fileName: fileData.fileName,
          fileSize: fileData.fileSize,
          mimeType: fileData.mimeType,
          progress: 0,
          moving: false,
          moved: false,
          error: null,
        };
      })
      .filter(Boolean);

    setMoveToManualResults(selectedMoveFiles);
    setIsMoveToManualDialogOpen(true);
  };

  const handleCloseMoveToManualDialog = () => {
    if (movingToManualInvoices) {
      return;
    }

    setIsMoveToManualDialogOpen(false);
  };

  const updateUploadFile = (fileId, nextValues) => {
    setUploadResults((prevFiles) =>
      prevFiles.map((fileObj) => (fileObj.id === fileId ? { ...fileObj, ...nextValues } : fileObj))
    );
  };

  const getCurrentUserId = async () => {
    const userId = globalData?.userId || sessionStorage.getItem("userId");
    if (userId) {
      return userId.toString();
    }

    await fetchUserData();
    return sessionStorage.getItem("userId")?.toString() || "";
  };

  const updateMoveToManualFile = (fileId, nextValues) => {
    setMoveToManualResults((prevFiles) =>
      prevFiles.map((fileObj) => (fileObj.id === fileId ? { ...fileObj, ...nextValues } : fileObj))
    );
  };

  const handleMoveToManualInvoicesClick = async () => {
    const filesToMove = moveToManualResults.filter((fileObj) => !fileObj.moved && !fileObj.error);
    if (filesToMove.length === 0) {
      return;
    }

    setMovingToManualInvoices(true);
    const movedFileIds = [];

    for (const fileObj of filesToMove) {
      try {
        updateMoveToManualFile(fileObj.id, { moving: true, error: null, progress: 25 });
        await axios.post(`/api/v1/CustomerFile/${fileObj.id}/move-to-manual-invoice-draft`);
        updateMoveToManualFile(fileObj.id, { moving: false, moved: true, progress: 100 });
        movedFileIds.push(fileObj.id);
      } catch (error) {
        updateMoveToManualFile(fileObj.id, {
          moving: false,
          progress: 100,
          error: error.response?.data || error.message || "Move failed.",
        });
      }
    }

    await fetchCustomerFiles();
    setSelectedFiles((prevSelectedFiles) => prevSelectedFiles.filter((fileId) => !movedFileIds.includes(fileId)));
    setMovingToManualInvoices(false);
  };

  const handleConfirmUpload = async () => {
    const filesToUpload = uploadResults.filter((fileObj) => !fileObj.uploaded && !fileObj.error);
    if (filesToUpload.length === 0) {
      return;
    }

    setUploading(true);
    const uploadedFileIds = [];

    try {
      setUploadResults((prevFiles) => prevFiles.map((fileObj) => ({ ...fileObj, error: null })));
      const uploadedBy = await getCurrentUserId();

      if (!uploadedBy) {
        setUploadResults((prevFiles) =>
          prevFiles.map((fileObj) => ({
            ...fileObj,
            progress: 100,
            error: "User id could not be found.",
          }))
        );
        return;
      }

      const batchInfo = {
        uploadedBy,
      };

      const batchResponse = await axios.post("/api/v1/OCRBatch", batchInfo);
      const batchId = batchResponse.data.batchId;

      console.log("Created Batch ID:", batchId);

      for (const fileObj of filesToUpload) {
        const fileData = tableData.find((file) => file.fileId === fileObj.id);
        if (!fileData) continue;

        updateUploadFile(fileObj.id, { uploading: true, error: null, progress: 10 });

        const fileInfo = {
          documentType: 1,
          processedByDWS: 1,
          deleted: 0,
          fileName: fileData.fileName,
          mimeType: fileData.mimeType,
          fileSize: fileData.fileSize,
          batchId: batchId,
          uploadDate: moment().format("YYYY-MM-DDTHH:mm:ss.SSS") + "Z",
          uploadedBy,
        };

        try {
          const fileResponse = await axios.post("/api/v1/OCRFile", fileInfo);
          const newFileId = fileResponse.data.fileId;

          updateUploadFile(fileObj.id, { progress: 30 });

          const blobResponse = await axios.get(`/api/v1/AzureCustomerBlobStorage/${fileObj.id}`, {
            responseType: "blob",
          });
          const blob = blobResponse.data;

          updateUploadFile(fileObj.id, { progress: 55 });

          const formData = new FormData();
          formData.append("file", blob, newFileId + ".pdf");

          const uploadResponse = await axios.post("/api/v1/azureocrblobstorage", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
            onUploadProgress: (progressEvent) => {
              const total = progressEvent.total || 1;
              const uploadProgress = Math.round((progressEvent.loaded * 30) / total);
              updateUploadFile(fileObj.id, { progress: Math.min(85, 55 + uploadProgress) });
            },
          });

          if (uploadResponse.status === 200) {
            const updateCustomerFile = await axios.put(
              `/api/v1/CustomerFileSetProcessedByDws/${fileObj.id}?action=UploadToOCRSystem&ocrBatchId=${batchId}`
            );
            console.log("Updated Customer File:", updateCustomerFile.data);

            setTableData((prevData) =>
              prevData.map((file) => (file.fileId === fileObj.id ? { ...file, processedByDWS: 1 } : file))
            );
            updateUploadFile(fileObj.id, { uploading: false, uploaded: true, progress: 100 });
            uploadedFileIds.push(fileObj.id);
          }
        } catch (error) {
          console.error(`Upload failed for file ID ${fileObj.id}:`, error);
          updateUploadFile(fileObj.id, {
            uploading: false,
            progress: 100,
            error: error.response?.data || error.message || "Upload failed.",
          });
        }
      }

      await fetchCustomerFiles();
      setSelectedFiles((prevSelectedFiles) => prevSelectedFiles.filter((fileId) => !uploadedFileIds.includes(fileId)));
    } catch (batchError) {
      console.error("Batch creation failed:", batchError);
      setUploadResults((prevFiles) =>
        prevFiles.map((fileObj) => ({
          ...fileObj,
          uploading: false,
          progress: 100,
          error: `Batch Error: ${batchError.response?.data || batchError.message}`,
        }))
      );
    } finally {
      setUploading(false);
    }
  };

  const handleCancelUpload = () => {
    if (uploading) {
      return;
    }

    setIsUploadDialogOpen(false);
  };

  const handleDownloadClick = async () => {
    if (selectedFiles.length === 0) return;
    setDownloadInProgress(true);

    const NameArr = selectedFiles
      .map((fileId) => {
        const fileData = tableData.find((file) => file.fileId === fileId);
        if (!fileData) return null;
        console.log("fileData", fileData);
        return [fileData.fileId.toString(), fileData.fileName]; // Convert fileId to string
      })
      .filter(Boolean);

    const ZipFileName = "DownloadedFiles";
    console.log("NameArr", NameArr);
    try {
      const response = await axios.post(
        "/api/v1/AzureCustomerBlobStorageBulk",
        {
          NameArr,
          ZipFileName,
        },
        {
          responseType: "blob",
        }
      );

      const href = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = href;
      link.setAttribute("download", `${ZipFileName}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error downloading bulk files:", error);
    } finally {
      setDownloadInProgress(false);
    }
  };

  // Handle key presses for +, -, ArrowUp, and ArrowDown keys
  const handleCellKeyDown = (params, event) => {
    if (event.key === "+" || event.key === "-") {
      event.preventDefault(); // Prevent default behavior

      const rowId = params.id;

      if (event.key === "+") {
        setSelectedFiles((prev) => {
          if (!prev.includes(rowId)) {
            return [...prev, rowId];
          }
          return prev;
        });
      } else if (event.key === "-") {
        setSelectedFiles((prev) => prev.filter((id) => id !== rowId));
      }
    } else if (event.key === "ArrowUp" || event.key === "ArrowDown") {
      // Delay execution to allow the focus to move to the next cell
      setTimeout(() => {
        const focusedCell = document.activeElement;
        const rowElement = focusedCell.closest("[data-id]");
        if (rowElement) {
          const rowId = rowElement.getAttribute("data-id");
          if (rowId && rowId !== highlightedRowId) {
            const rowData = tableData.find((row) => row.fileId.toString() === rowId);
            if (rowData) {
              const newParams = { id: rowId, row: rowData };
              openRowDetails(newParams);
            }
          }
        }
      }, 0);
    }
  };

  // Handle pane resize
  const handlePaneChange = (size) => {
    setRightPaneSize(size);
    if (size !== 0 && size !== 0) {
      setLastRightPaneSize(size);
    }
  };

  const [pageSize, setPageSize] = useState(15);
  const [userPageSize, setUserPageSize] = useState(null);

  useEffect(() => {
    const updatePageSize = () => {
      const gridHeight = containerRef.current ? containerRef.current.clientHeight : 0;
      const newPageSize = Math.floor((gridHeight - 305) / 37);
      if (userPageSize === null) {
        setUserPageSize(newPageSize);
        setPageSize(newPageSize);
      }
    };

    updatePageSize();
    window.addEventListener("resize", updatePageSize);
    return () => window.removeEventListener("resize", updatePageSize);
  }, [containerRef, userPageSize]);

  const CustomFooter = (props) => {
    return (
      <GridFooterContainer
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          p: 1,
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" sx={{ ...getThemeTextColor(theme) }}>
            Rows per page:{" "}
          </Typography>
          <FormControl variant="outlined" size="small" sx={{ minWidth: 70 }}>
            <Select
              id="page-size-select"
              value={userPageSize || ""}
              onChange={(e) => {
                setUserPageSize(e.target.value);
                setPageSize(e.target.value);
              }}
              sx={{ ...getThemeTextColor(theme) }}
            >
              {[5, 10, 15, 20, 25, 50, 100].map((size) => (
                <MenuItem key={size} value={size}>
                  {size}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <GridPagination {...props} />
      </GridFooterContainer>
    );
  };

  return (
    <React.Fragment>
      <Helmet title="Customer Uploaded Invoices" />
      <Box display="flex" justifyContent="space-between" mb={2}>
        <Typography variant="h3" gutterBottom>
          Customer Uploaded Invoices
        </Typography>
        <Breadcrumbs aria-label="Breadcrumb">
          <NavLink to="/">Dashboard</NavLink>
          <NavLink to="/">Pages</NavLink>
          <Typography>Customer Uploaded Invoices</Typography>
        </Breadcrumbs>
      </Box>

      <Divider my={6} />

      <div ref={containerRef} style={{ height: "calc(100vh - 200px)", position: "relative" }}>
        <PanelGroup
          direction="horizontal"
          style={{ height: "100%" }}
          onUpdate={(sizes) => handlePaneChange(sizes[1])} // sizes array: [left, right]
          defaultSizes={[0.5, 0]} // Initial left pane at 50%, right pane hidden
        >
          {/* Left Panel */}
          <Panel minSize={0} style={{ overflow: "hidden" }}>
            <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
              <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} indicatorColor="primary" textColor="primary">
                <Tab label="Customer Uploads" />
                <Tab label="Historic Uploads" />
              </Tabs>
              <div style={{ flexGrow: 1, overflow: "auto" }}>
                <Card style={{ height: "100%", display: "flex", flexDirection: "column" }}>
                  <CardContent style={{ paddingBottom: 16 }}>
                    {tabValue === 0 ? (
                      <>
                        <Typography variant="h6" gutterBottom>
                          Customer Uploaded Invoices
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          In this table you can search and view customer uploaded invoices.
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Typography variant="h6" gutterBottom>
                          Processed Customer Uploaded Invoices
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          In this table you can search and view historic customer uploaded invoices.
                        </Typography>
                      </>
                    )}
                  </CardContent>
                  <div style={{ flexGrow: 1 }}>
                    <DataGrid
                      getRowId={(row) => row.fileId}
                      disableSelectionOnClick={true}
                      components={{ Toolbar: GridToolbar, Footer: CustomFooter }}
                      density="compact"
                      rowsPerPageOptions={[]} // Voeg dit toe
                      componentsProps={{
                        toolbar: {
                          csvOptions: { disableToolbarButton: false },
                          printOptions: { disableToolbarButton: false },
                          showQuickFilter: true,
                          quickFilterProps: { debounceMs: 250 },
                        },
                      }}
                      rows={(tabValue === 0
                        ? tableData.filter((row) => row.processedByDWS !== 1)
                        : tableData.filter((row) => row.processedByDWS === 1)
                      ).sort((a, b) => new Date(b.creationDate) - new Date(a.creationDate))}
                      columns={columns}
                      pageSize={pageSize}
                      checkboxSelection={tabValue === 0}
                      selectionModel={selectedFiles}
                      onSelectionModelChange={(newSelection) => {
                        setSelectedFiles(newSelection);
                      }}
                      onRowClick={handleRowClick}
                      getRowClassName={(params) => (params.id === highlightedRowId ? "highlighted-row" : "")}
                      onCellKeyDown={handleCellKeyDown}
                      sx={{
                        height: "100%",
                        width: "100%",
                        borderRadius: 1,
                        backgroundColor: "background.paper",
                        boxShadow: 2,
                        "& .MuiDataGrid-cell:hover": {
                          color: "primary.main",
                        },
                        "& .highlighted-row": {
                          backgroundColor: "rgba(25, 118, 210, 0.08)",
                          "&:hover": {
                            backgroundColor: "rgba(25, 118, 210, 0.15)",
                          },
                        },
                      }}
                    />
                  </div>
                  {/* Reserve fixed space for buttons */}
                  <Box display="flex" gap={2} mt={2} mb={2} style={{ minHeight: "34px", flexWrap: "wrap" }}>
                    {tabValue === 0 && (
                      <>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleDownloadClick}
                          disabled={selectedFiles.length === 0 || downloadInProgress}
                        >
                          {downloadInProgress ? (
                            <Box display="flex" alignItems="center">
                              Downloading...
                              <CircularProgress size={20} sx={{ ml: 1 }} />
                            </Box>
                          ) : (
                            "Download Selected Files"
                          )}
                        </Button>
                        <Button variant="contained" color="warning" onClick={handleUploadClick} disabled={selectedFiles.length === 0}>
                          Upload to OCR System
                        </Button>
                        <Button
                          variant="contained"
                          color="info"
                          onClick={handleOpenMoveToManualDialog}
                          disabled={movingToManualInvoices || selectedFiles.length === 0}
                        >
                          {movingToManualInvoices ? (
                            <Box display="flex" alignItems="center">
                              Moving...
                              <CircularProgress size={20} sx={{ ml: 1 }} />
                            </Box>
                          ) : (
                            "Move to Manual Invoices"
                          )}
                        </Button>
                        <Button
                          type="button"
                          onClick={() => setIsConfirmDialogOpen(true)}
                          variant="contained"
                          color="success"
                          disabled={setProcessedByDwsInProgress || selectedFiles.length === 0}
                        >
                          {setProcessedByDwsInProgress ? (
                            <Box display="flex" alignItems="center">
                              Processing...
                              <CircularProgress size={20} sx={{ ml: 1 }} />
                            </Box>
                          ) : (
                            "Set Processed By DWS"
                          )}
                        </Button>
                      </>
                    )}
                  </Box>
                </Card>
              </div>
            </div>
          </Panel>

          {/* Resize Handle */}
          <PanelResizeHandle
            style={{
              width: rightPaneSize === 0 ? "0px" : "5px",
              cursor: rightPaneSize === 0 ? "default" : "col-resize",
              backgroundColor: rightPaneSize === 0 ? "transparent" : "#ddd",
              borderLeft: rightPaneSize === 0 ? "none" : "1px solid #aaa",
            }}
          />

          {/* Right Panel */}
          <Panel minSize={0} style={{ overflow: "hidden", display: rightPaneSize === 0 ? "none" : "block" }}>
            {rightPaneSize !== 0 && (
              <div style={{ width: "100%", height: "100%" }}>
                <Card style={{ height: "100%", position: "relative" }}>
                  <CardContent style={{ height: "100%", paddingBottom: 16 }}>
                    <IconButton
                      onClick={() => {
                        setRightPaneSize(0); // Close the right pane
                        setHighlightedRowId(null); // Optionally clear the highlighted row
                        setViewCustomerInvoiceDialog({
                          ...viewCustomerInvoiceDialog,
                          isOpen: false,
                        });
                      }}
                      style={{ position: "absolute", top: 8, right: 8 }}
                    >
                      <CloseIcon />
                    </IconButton>
                    <ViewCustomerInvoiceUploadDialog
                      viewCustomerInvoiceDialog={viewCustomerInvoiceDialog}
                      setViewCustomerInvoiceDialog={setViewCustomerInvoiceDialog}
                      countryArr={countryData}
                      supplierArr={supplierData}
                      currencyArr={currencyData}
                      productTypeArr={productData}
                      invoiceStateArr={invoiceStateData}
                      refreshGrid={fetchCustomerFiles}
                    />
                  </CardContent>
                </Card>
              </div>
            )}
          </Panel>
        </PanelGroup>
      </div>
      <>
        <Dialog
          open={isUploadDialogOpen}
          onClose={handleCancelUpload}
          aria-labelledby="upload-confirmation-dialog-title"
          aria-describedby="upload-confirmation-dialog-description"
          maxWidth="md"
          fullWidth
        >
          <DialogTitle id="upload-confirmation-dialog-title">Upload to OCR System</DialogTitle>
          <DialogContent>
            <DialogContentText id="upload-confirmation-dialog-description">
              Review the selected customer files and click Upload to upload them to the OCR system.
            </DialogContentText>
            <Paper sx={{ height: 420, mt: 2, mb: 2, overflow: "hidden", position: "relative" }}>
              <Box sx={{ height: "calc(100% - 50px)", overflow: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      {["Customer", "File Name", "File Size", "MIME Type", "Progress", "Status"].map((header) => (
                        <th
                          key={header}
                          style={{
                            height: 30,
                            borderBottom: "1px solid #ccc",
                            padding: "4px 8px",
                            textAlign: "left",
                          }}
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {uploadResults.length === 0 ? (
                      <tr>
                        <td colSpan={6} style={{ padding: 12, color: "#666" }}>
                          No customer files selected.
                        </td>
                      </tr>
                    ) : (
                      uploadResults.map((fileObj) => (
                        <tr key={fileObj.id}>
                          <td style={{ height: 34, borderBottom: "1px solid #eee", padding: "4px 8px" }}>
                            {fileObj.customerLabel}
                          </td>
                          <td style={{ height: 34, borderBottom: "1px solid #eee", padding: "4px 8px" }}>
                            {fileObj.fileName}
                          </td>
                          <td style={{ height: 34, borderBottom: "1px solid #eee", padding: "4px 8px" }}>
                            {fileObj.fileSize ? `${(fileObj.fileSize / 1024).toFixed(2)} KB` : ""}
                          </td>
                          <td style={{ height: 34, borderBottom: "1px solid #eee", padding: "4px 8px" }}>
                            {fileObj.mimeType}
                          </td>
                          <td style={{ height: 34, borderBottom: "1px solid #eee", padding: "4px 8px" }}>
                            <LinearProgress variant="determinate" value={fileObj.progress} />
                          </td>
                          <td style={{ height: 34, borderBottom: "1px solid #eee", padding: "4px 8px" }}>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1 }}>
                              {fileObj.uploaded ? (
                                <CheckCircleIcon color="success" fontSize="small" />
                              ) : fileObj.error ? (
                                <Tooltip title={fileObj.error}>
                                  <ErrorIcon color="error" fontSize="small" />
                                </Tooltip>
                              ) : fileObj.uploading ? (
                                <CircularProgress size={18} />
                              ) : (
                                <Typography variant="body2">Ready</Typography>
                              )}
                            </Box>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </Box>
              <Box
                sx={{
                  height: 50,
                  display: "flex",
                  alignItems: "center",
                  px: 2,
                  borderTop: "1px solid #ccc",
                  position: "absolute",
                  bottom: 0,
                  width: "100%",
                  backgroundColor: "background.paper",
                }}
              >
                <Typography variant="body2">
                  Total files to be uploaded: {uploadResults.filter((fileObj) => !fileObj.error && !fileObj.uploaded).length}
                </Typography>
              </Box>
            </Paper>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleConfirmUpload}
              color="warning"
              variant="contained"
              autoFocus
              disabled={uploading || uploadResults.filter((fileObj) => !fileObj.error && !fileObj.uploaded).length === 0}
            >
              {uploading ? "Uploading..." : "Upload"}
            </Button>
            <Button onClick={handleCancelUpload} color="primary" disabled={uploading}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={isMoveToManualDialogOpen}
          onClose={handleCloseMoveToManualDialog}
          aria-labelledby="move-to-manual-dialog-title"
          aria-describedby="move-to-manual-dialog-description"
          maxWidth="md"
          fullWidth
        >
          <DialogTitle id="move-to-manual-dialog-title">Move to Manual Invoices</DialogTitle>
          <DialogContent>
            <DialogContentText id="move-to-manual-dialog-description">
              Review the selected customer files and click Upload to move them to Manual Invoice Drafts.
            </DialogContentText>
            <Paper sx={{ height: 420, mt: 2, mb: 2, overflow: "hidden", position: "relative" }}>
              <Box sx={{ height: "calc(100% - 50px)", overflow: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      {["Customer", "File Name", "File Size", "MIME Type", "Progress", "Status"].map((header) => (
                        <th
                          key={header}
                          style={{
                            height: 30,
                            borderBottom: "1px solid #ccc",
                            padding: "4px 8px",
                            textAlign: "left",
                          }}
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {moveToManualResults.length === 0 ? (
                      <tr>
                        <td colSpan={6} style={{ padding: 12, color: "#666" }}>
                          No customer files selected.
                        </td>
                      </tr>
                    ) : (
                      moveToManualResults.map((fileObj) => (
                        <tr key={fileObj.id}>
                          <td style={{ height: 34, borderBottom: "1px solid #eee", padding: "4px 8px" }}>
                            {fileObj.customerLabel}
                          </td>
                          <td style={{ height: 34, borderBottom: "1px solid #eee", padding: "4px 8px" }}>
                            {fileObj.fileName}
                          </td>
                          <td style={{ height: 34, borderBottom: "1px solid #eee", padding: "4px 8px" }}>
                            {fileObj.fileSize ? `${(fileObj.fileSize / 1024).toFixed(2)} KB` : ""}
                          </td>
                          <td style={{ height: 34, borderBottom: "1px solid #eee", padding: "4px 8px" }}>
                            {fileObj.mimeType}
                          </td>
                          <td style={{ height: 34, borderBottom: "1px solid #eee", padding: "4px 8px" }}>
                            <LinearProgress variant="determinate" value={fileObj.progress} />
                          </td>
                          <td style={{ height: 34, borderBottom: "1px solid #eee", padding: "4px 8px" }}>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1 }}>
                              {fileObj.moved ? (
                                <CheckCircleIcon color="success" fontSize="small" />
                              ) : fileObj.error ? (
                                <Tooltip title={fileObj.error}>
                                  <ErrorIcon color="error" fontSize="small" />
                                </Tooltip>
                              ) : fileObj.moving ? (
                                <CircularProgress size={18} />
                              ) : (
                                <Typography variant="body2">Ready</Typography>
                              )}
                            </Box>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </Box>
              <Box
                sx={{
                  height: 50,
                  display: "flex",
                  alignItems: "center",
                  px: 2,
                  borderTop: "1px solid #ccc",
                  position: "absolute",
                  bottom: 0,
                  width: "100%",
                  backgroundColor: "background.paper",
                }}
              >
                <Typography variant="body2">
                  Total files to be uploaded: {moveToManualResults.filter((fileObj) => !fileObj.error && !fileObj.moved).length}
                </Typography>
              </Box>
            </Paper>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleMoveToManualInvoicesClick}
              color="info"
              variant="contained"
              autoFocus
              disabled={
                movingToManualInvoices || moveToManualResults.filter((fileObj) => !fileObj.error && !fileObj.moved).length === 0
              }
            >
              {movingToManualInvoices ? "Uploading..." : "Upload"}
            </Button>
            <Button onClick={handleCloseMoveToManualDialog} color="primary" disabled={movingToManualInvoices}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

      </>
      <Dialog open={isConfirmDialogOpen} onClose={() => setIsConfirmDialogOpen(false)}>
        <DialogTitle>Confirm</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to move these invoices to the historic list?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsConfirmDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={() => {
              handleProcessedByDWSClick();
              setIsConfirmDialogOpen(false);
            }}
            color="warning"
            autoFocus
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default CustomerDataGrid;
